// The admin Logs tabs. Each tab is one server log group (getTrustCenterActivity
// `group`, see the API's change_log.js); "all" asks for every event.
export const LOG_TABS = [
  { value: "all", label: "All" },
  { value: "visitors", label: "Visitors" },
  { value: "resources", label: "Resources" },
  { value: "compliance", label: "Compliance" },
  { value: "controls", label: "Controls" },
  { value: "subprocessors", label: "Subprocessors" },
  { value: "faq", label: "FAQ" },
  { value: "updates", label: "Updates" },
  { value: "publishing", label: "Publishing" },
];

export const logEventLabels = {
  page_view: "Opened page",
  nda_accepted: "NDA accepted",
  resource_opened: "Opened resource",
  access_denied: "Access denied",
  access_requested: "Access requested",
  access_granted: "Access granted",
  access_revoked: "Access revoked",
  access_link_sent: "Access link sent",
  resource_emailed: "Document emailed",
  updates_subscribed: "Subscribed to updates",
  resource_added: "Resource added",
  resource_removed: "Resource removed",
  resource_visibility_changed: "Visibility changed",
  resource_changed: "Resource edited",
  category_added: "Category added",
  category_removed: "Category removed",
  category_changed: "Category edited",
  framework_added: "Framework added",
  framework_removed: "Framework removed",
  framework_changed: "Framework edited",
  control_visibility_changed: "Control visibility changed",
  subprocessor_added: "Subprocessor added",
  subprocessor_removed: "Subprocessor removed",
  subprocessor_changed: "Subprocessor edited",
  subprocessor_shown: "Subprocessor shown",
  subprocessor_hidden: "Subprocessor hidden",
  faq_added: "FAQ added",
  faq_removed: "FAQ removed",
  faq_changed: "FAQ edited",
  update_added: "Update added",
  update_removed: "Update removed",
  update_changed: "Update edited",
  update_published: "Update published",
  published: "Published",
  unpublished: "Unpublished",
};

export const eventLabel = (event) => logEventLabels[event] || event;

export const parseMetadata = (metadata) => {
  if (!metadata) return {};
  if (typeof metadata === "object") return metadata;
  try {
    return JSON.parse(metadata) || {};
  } catch {
    return {};
  }
};

const showValue = (value) =>
  value === "" || value == null ? "empty" : String(value);

const countPart = (n, word) =>
  Number(n) > 0 ? `${n} ${word}` : null;

// One section of a publish: "Resources: 1 added, 2 changed".
export const sectionChangeText = (change) => {
  const bits = [
    countPart(change.added, "added"),
    countPart(change.removed, "removed"),
    countPart(change.changed, "changed"),
  ].filter(Boolean);
  if (!change.label) return bits.join(", ");
  return bits.length ? `${change.label}: ${bits.join(", ")}` : change.label;
};

export const publishWhere = (metadata) => {
  const m = parseMetadata(metadata);
  if (m.customDomain) return m.customDomain;
  if (m.slug) return `/trust/${m.slug}`;
  return "";
};

// Where visitors actually saw the page (Regodit link vs the company's domain).
export const publishShownAt = (metadata) => {
  const m = parseMetadata(metadata);
  if (m.target === "custom")
    return m.customDomain
      ? `Custom domain · ${m.customDomain}`
      : "Custom domain";
  if (m.slug) return `Regodit · /trust/${m.slug}`;
  if (m.target === "regodit") return "Regodit";
  return publishWhere(m);
};

const fieldChangeText = (change) =>
  `${showValue(change.from)} → ${showValue(change.to)}`;

// Labelled lines for the expanded row. Publishing lists each section's
// added / removed / changed counts, where it went live, and whether only
// the domain changed.
export const logDetailLines = (event, metadata) => {
  const m = parseMetadata(metadata);
  const lines = [];
  const add = (label, value) => {
    if (value) lines.push({ label, value: String(value) });
  };

  if (event === "published") {
    if (m.version) add("Version", m.version);
    add("Shown at", publishShownAt(m));
    if (m.contentUnchanged)
      add("Content", "Unchanged — only where the page lives changed.");
    (m.changes || []).forEach((change) => {
      const text = sectionChangeText(change);
      const value = change.label && text.startsWith(`${change.label}: `)
        ? text.slice(change.label.length + 2)
        : text === change.label
          ? "Updated"
          : text;
      add(change.label || "Section", value);
    });
    if (m.acknowledged) add("Notice", "Accepted before publishing");
    return lines;
  }

  if (event === "unpublished") {
    add("Was shown at", publishShownAt(m));
    add(
      "Status",
      "Taken offline. The link is kept so it can be published again."
    );
    return lines;
  }

  (m.changes || []).forEach((change) =>
    add(change.label || "Changed", fieldChangeText(change))
  );
  if (m.visibility && !lines.some((l) => l.label === "Visibility"))
    add("Visibility", m.visibility);
  if (m.category && /^resource_(added|removed)$/.test(event))
    add("Category", m.category);
  if (m.status) add("Status", m.status);
  if (event === "subprocessor_added" && m.shown === false)
    add("Visibility", "Hidden until shown");
  if (m.source === "integration") add("Source", "Connected integration");
  if (m.reason) add("Reason", m.reason);
  if (m.company) add("Company", m.company);
  return lines;
};

// One line for the table, for scanning. Publishing includes version, each
// section's counts, and the public URL.
export const logDetails = (event, metadata) => {
  const m = parseMetadata(metadata);
  if (event === "published") {
    const sections = (m.changes || []).map(sectionChangeText).filter(Boolean);
    if (m.contentUnchanged)
      return ["Domain only", publishWhere(m)].filter(Boolean).join(" · ");
    return [
      m.version ? `Version ${m.version}` : null,
      sections.join(" · ") || null,
      publishWhere(m) || null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  if (event === "unpublished") {
    const where = publishWhere(m);
    return where ? `Taken offline · ${where}` : "Taken offline";
  }
  return logDetailLines(event, metadata)
    .map((line) =>
      line.label === "Changed" ? line.value : `${line.label}: ${line.value}`
    )
    .join(" · ");
};
